/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { iconCheckmarkCircle } from '@/components/icons';
import { productStockStore } from '@/stores/store.product-stock';
import { baseStyles, skeletonStyles } from '@/styles';
import { i18nInit, setBaseUrl } from '@/utils';

import '@/components/accessibility/fg-aria-live';

@customElement('fg-stock')
export class FgStock extends LitElement {
  private unsubscribe?: () => void;
  private productStockStore = productStockStore.getState();

  @property({ type: String })
  locale = 'en_US';

  @property({ type: String })
  baseUrl = '';

  @property({ type: String })
  product = '';

  @property({ type: Boolean })
  loading = this.productStockStore.loading;

  @property({ type: Object })
  productStock = this.productStockStore.productStock;

  @property({ type: Boolean })
  error = this.productStockStore.error;

  connectedCallback() {
    super.connectedCallback();

    this.unsubscribe = productStockStore.subscribe(state => {
      this.productStock = state.productStock;
      this.loading = state.loading;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.unsubscribe?.();
  }

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  willUpdate(changedProperties: PropertyValues) {
    const { setConfig, getProductStock } = this.productStockStore;
    if (changedProperties.has('product') || changedProperties.has('locale')) {
      setConfig({
        locale: this.locale,
        product: this.product,
      });

      getProductStock();
    }
  }

  render() {
    if ((!this.loading && !this.productStock?.label) || this.error) {
      return nothing;
    }
    return html`
      <fg-aria-live
        .announcement=${t('accessibility.productStockAnnouncement', {
          label: this.productStock?.label,
        })}
      ></fg-aria-live>
      ${this.loading
        ? html`
            <div class="skeleton-stock"></div>
          `
        : html`
            ${iconCheckmarkCircle}
            <div class="stock-text">${this.productStock?.label}</div>
          `}
    `;
  }

  static styles = [
    baseStyles,
    skeletonStyles,
    css`
      :host {
        display: block;
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
        font-weight: 600;
        font-size: 1rem;
        display: flex;
        align-items: center;
      }

      .stock-text {
        margin-left: 8px;
      }

      .skeleton-stock {
        width: 200px;
        height: 24px;
        background-color: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
      }
    `,
  ];
}
